/**
 * useSiteLanguage — LA langue d'interface des pages publiques.
 *
 * Avant ce fichier, chaque page qui voulait connaître la langue se la
 * recopiait : `PublicHeader` la tenait dans un `useState` local, le footer
 * avait sa propre lecture de `localStorage` avec son propre abonnement,
 * `TeamPage` et `HowItWorksV2` en gardaient une troisième copie alimentée
 * par le callback `onLanguageChange` du header. Trois lectures, trois
 * moments de synchronisation, et rien qui garantisse qu'elles disent la
 * même chose : une page montée pendant qu'une autre écrivait le store
 * pouvait rester sur l'ancienne langue jusqu'au rechargement.
 *
 * Ici, une seule lecture (`archer_locale`) et un seul abonnement :
 *  · `LOCALE_CHANGE_EVENT` — le sélecteur de l'onglet courant ;
 *  · `storage`             — les autres onglets ouverts sur le site.
 *
 * Le store `archer_locale` reste la source de vérité persistée : c'est lui
 * qui survit au rechargement et à la navigation entre pages. Ce hook n'en
 * est que la vue réactive.
 *
 * `getStoredLocale()` n'est PAS utilisé pour lire : il retombe sur 'us-en'
 * quand rien n'est stocké, donc un visiteur qui n'a jamais rien choisi
 * serait servi en anglais alors que le seul marché actif est belge
 * francophone. On lit la clé brute et on ne fait confiance qu'à un choix
 * réellement écrit ; sinon FR.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  LOCALE_CHANGE_EVENT,
  setStoredLocale,
} from '../data/landingTranslations';

/** Les trois langues exposées par le sélecteur du header. */
export const SITE_LANGUAGES = ['fr', 'nl', 'en'];

/** Langue servie quand rien n'a été choisi — la Belgique est le seul marché actif. */
export const DEFAULT_SITE_LANGUAGE = 'fr';

/** Lecture brute du store, sans abonnement. Utile hors composant. */
export function readSiteLanguage() {
  try {
    const stored = localStorage.getItem('archer_locale');
    const lang = stored ? (stored.split('-')[1] || DEFAULT_SITE_LANGUAGE) : DEFAULT_SITE_LANGUAGE;
    return SITE_LANGUAGES.includes(lang) ? lang : DEFAULT_SITE_LANGUAGE;
  } catch (_) {
    return DEFAULT_SITE_LANGUAGE;
  }
}

/** Juridiction courante, même store, même défaut belge. */
export function readSiteCountry() {
  try {
    const stored = localStorage.getItem('archer_locale');
    return (stored ? (stored.split('-')[0] || 'be') : 'be').toUpperCase();
  } catch (_) {
    return 'BE';
  }
}

/**
 * Écrit la langue choisie. Toute juridiction est liée à `be-` : la Belgique
 * est la seule active (gel post-M6), et le sélecteur du header ne propose
 * que FR/NL/EN. Émet `LOCALE_CHANGE_EVENT`, donc tout ce qui monte ce hook
 * se re-rend — header, footer et corps de page compris.
 */
export function writeSiteLanguage(lang) {
  const l = String(lang || DEFAULT_SITE_LANGUAGE).toLowerCase();
  const next = SITE_LANGUAGES.includes(l) ? l : DEFAULT_SITE_LANGUAGE;
  setStoredLocale(`be-${next}`);
  return next;
}

/**
 * Langue d'interface courante, tenue à jour à chaud.
 * Retourne `[lang, setLang]` — `setLang` persiste ET prévient tout le monde.
 */
export function useSiteLanguage() {
  const [lang, setLang] = useState(readSiteLanguage);

  useEffect(() => {
    const sync = () => setLang(readSiteLanguage());
    // Un composant monté APRÈS une écriture aurait raté l'événement : on
    // relit une fois au montage plutôt que de faire confiance au seul
    // état initial, qui date de la première frame du composant.
    sync();
    window.addEventListener(LOCALE_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LOCALE_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const set = useCallback((next) => {
    // Pas de `setLang` local ici : `writeSiteLanguage` émet l'événement,
    // et c'est l'abonnement ci-dessus qui met à jour l'état. Un seul
    // chemin de mise à jour, donc aucune divergence possible entre le
    // composant qui écrit et ceux qui écoutent.
    writeSiteLanguage(next);
  }, []);

  return [lang, set];
}

export default useSiteLanguage;
